import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { message } from '@bb/elements-web';
import { getKeyholderBillingProfile } from 'api/SupraController';
import type { IKeyholderDataWeb } from '@/app-package';
import { SupraController } from 'api';
import { AuthContext } from 'contexts';

// ============================================================================
// Types
// ============================================================================

interface SubscriptionsPageContextShape {
  /** The credit card billing info for this user */
  billingProfile: IKeyholderDataWeb;
  /** Whether we are still fetching the billing profile */
  loadingBillingProfile: boolean;
  /** The ID of the brokerage this user belongs to */
  orgId: string;
  refetchBillingProfile: () => Promise<void>;
  updateBillingProfile: (
    updatedBillingProfile: IKeyholderDataWeb,
    resetPaymentStatus?: boolean,
  ) => Promise<void>;
  updateProfileError: Error | undefined;
  fetchProfileError: Error | undefined;
  isUpdatingProfile: boolean;
  isFetchingProfile: boolean;
  hasSavedCard: boolean;
}

interface SubscriptionsPageProviderProps {
  orgId: string;
  billingCustomerNumber?: string;
  children: React.ReactNode;
}

/**
 * Indicates (via `response`) whether the user has permission
 * to manage some aspect of the billing profile.
 *
 * If the user does not have permission, the `message` field
 * will contain the reason why.
 */
export type CanManageBillingProfileType =
  | { response: true }
  | { response: false; message: string };

// ============================================================================
// Context
// ============================================================================

const SubscriptionsPageContext =
  createContext<SubscriptionsPageContextShape | null>(null);

/**
 * Hook to consume SubscriptionsPageContext.
 * Throws error if used outside of provider.
 */
export function useSubscriptionsPageContext(): SubscriptionsPageContextShape {
  const context = useContext(SubscriptionsPageContext);
  if (!context) {
    throw new Error(
      'useSubscriptionsPageContext must be used within a SubscriptionsPageProvider.',
    );
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

/**
 * Provides shared billing profile data to all components on the Subscriptions & Payments page.
 * Fetches billing profile once and shares it with child components.
 */
export const SubscriptionsPageProvider: React.FC<
  SubscriptionsPageProviderProps
> = ({ orgId, billingCustomerNumber, children }) => {
  const { fetchAuth } = useContext(AuthContext);

  const [billingProfile, setBillingProfile] = useState<IKeyholderDataWeb>();
  const [loadingBillingProfile, setLoadingBillingProfile] = useState(false);
  const [updateProfileError, setUpdateProfileError] = useState<Error>();
  const [fetchProfileError, setFetchProfileError] = useState<Error>();
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);

  const fetchBillingProfile = useCallback(async () => {
    if (!orgId || !billingCustomerNumber) {
      setBillingProfile(null);
      message.error(
        'Unable to load billing information. Please contact support.',
      );
      return;
    }

    setLoadingBillingProfile(true);
    setFetchProfileError(null);

    try {
      const profile = await getKeyholderBillingProfile({
        orgId,
        billingCustomerNumber,
      });

      setBillingProfile(profile);
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error('Failed to fetch billing profile');
      setFetchProfileError(err);
      setBillingProfile(null);
      message.error('Failed to load billing information.');
    } finally {
      setLoadingBillingProfile(false);
    }
  }, [orgId, billingCustomerNumber]);

  useEffect(() => {
    fetchBillingProfile();
  }, [fetchBillingProfile]);

  const hasSavedCard = useMemo(
    () => Boolean(billingProfile?.CCNumber && billingProfile?.CardholderName),
    [billingProfile?.CCNumber, billingProfile?.CardholderName],
  );

  const updateBillingProfile = async (
    updatedBillingProfile: IKeyholderDataWeb,
    resetPaymentStatus?: boolean,
  ) => {
    if (!updatedBillingProfile) {
      setUpdateProfileError(new Error('No billing profile provided'));
      return;
    }

    setIsUpdatingProfile(true);
    const profile = await SupraController.updateKeyholderBillingProfile({
      billingProfile: updatedBillingProfile,
      resetPaymentStatus: resetPaymentStatus ?? false,
    }).catch(error => {
      // Set error state based on the error returned from the API
      if ('OperationMessage' in error) {
        setUpdateProfileError(new Error(error.OperationMessage));
      } else if (error instanceof Error) {
        setUpdateProfileError(error);
      } else {
        setUpdateProfileError(
          new Error(
            'Unable to update billing profile. Please try again later.',
          ),
        );
      }

      // Ensure loading state is set to false
      setIsUpdatingProfile(false);

      // Re-throw the error to be caught by the caller
      throw error;
    });

    // Update state
    await fetchAuth();
    setBillingProfile(profile);
    setUpdateProfileError(undefined);
    setIsUpdatingProfile(false);
  };

  useEffect(() => {
    if (billingCustomerNumber === undefined) {
      setFetchProfileError(new Error('No billing customer number provided'));
      return;
    }

    if (orgId === undefined) {
      setFetchProfileError(new Error('No org id provided'));
      return;
    }

    setIsFetchingProfile(true);
    SupraController.getKeyholderBillingProfile({ orgId, billingCustomerNumber })
      .then(profile => {
        if (profile === undefined) {
          throw new Error('No billing profile found');
        }
        setBillingProfile(profile);
        setFetchProfileError(undefined);
      })
      .catch(() =>
        setFetchProfileError(new Error('Billing profile cannot be retrieved')),
      )
      .finally(() => {
        setIsFetchingProfile(false);
      });
  }, [billingCustomerNumber, orgId]);

  return (
    <SubscriptionsPageContext.Provider
      value={{
        billingProfile,
        loadingBillingProfile,
        orgId,
        refetchBillingProfile: fetchBillingProfile,
        updateBillingProfile,
        updateProfileError,
        fetchProfileError,
        isFetchingProfile,
        isUpdatingProfile,
        hasSavedCard,
      }}
    >
      {children}
    </SubscriptionsPageContext.Provider>
  );
};

export default SubscriptionsPageContext;
